'use client';

import { c as _c } from "react/compiler-runtime";
import { Spinner } from '@common/components';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { Image } from '@/components/client';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useSocialLogin } from '@/hooks/useSocialLogin';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { PartnersBlurOverlay } from './PartnersBlurOverlay';
type SocialType = 'kakao' | 'naver' | 'google' | 'apple';
const SOCIAL_LABEL: Record<SocialType, string> = {
  kakao: '카카오로 시작하기',
  naver: '네이버로 시작하기',
  google: 'Google로 시작하기',
  apple: 'Apple로 시작하기'
};
const SOCIAL_COLOR: Record<SocialType, string> = {
  kakao: 'bg-Y-400 text-Gray-900',
  naver: 'bg-[#20cf5d] text-Gray-white',
  google: 'border border-Gray-300 bg-Gray-white text-Gray-900',
  apple: 'bg-Gray-black text-Gray-white'
};
const SOCIAL_INTERACTION = 'transition-opacity duration-150 hover:opacity-80 active:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500';
interface SocialLinkProps {
  type: SocialType;
  redirectPath: string;
  isIconOnly?: boolean;
}
const SocialLink = t0 => {
  const $ = _c(16);
  const {
    type,
    redirectPath,
    isIconOnly: t1
  } = t0;
  const isIconOnly = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== redirectPath || $[1] !== type) {
    t2 = {
      type,
      redirectPath
    };
    $[0] = redirectPath;
    $[1] = type;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    startLogin,
    isPending
  } = useSocialLogin(t2);
  const t3 = SOCIAL_LABEL[type];
  const t4 = isIconOnly ? "size-48 rounded-pill" : "h-48 w-full rounded-small";
  const t5 = SOCIAL_COLOR[type];
  let t6;
  if ($[3] !== t4 || $[4] !== t5) {
    t6 = cn("flex items-center justify-center gap-8 font-700 typo-body3", t4, t5, SOCIAL_INTERACTION);
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== isPending || $[7] !== type) {
    t7 = isPending ? <Spinner aria-hidden={true} color="currentColor" size={24} /> : <Image unoptimized={true} alt="" height={24} src={`/assets/pngs/login/${type}.png`} width={24} />;
    $[6] = isPending;
    $[7] = type;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = isIconOnly ? null : SOCIAL_LABEL[type];
  let t9;
  if ($[9] !== isPending || $[10] !== startLogin || $[11] !== t3 || $[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = <button aria-label={t3} aria-busy={isPending} disabled={isPending} className={t6} onClick={startLogin}>{t7}{t8}</button>;
    $[9] = isPending;
    $[10] = startLogin;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
};
interface PartnersLoginPanelProps {
  redirectPath?: string;
  onClose?: () => void;
}
export const PartnersLoginPanel = t0 => {
  const $ = _c(30);
  const {
    redirectPath,
    onClose
  } = t0;
  const currentPath = usePathname() ?? "";
  const pathname = redirectPath ?? currentPath;
  const {
    isWelloApp
  } = useCheckWelloApp();
  const isAndroid = useDeviceInfoStore(_temp);
  const isAppleHidden = isAndroid && isWelloApp;
  let t1;
  let t2;
  let t3;
  if ($[0] !== pathname) {
    t1 = <SocialLink type="kakao" redirectPath={pathname} />;
    t2 = <SocialLink type="naver" redirectPath={pathname} />;
    t3 = <SocialLink type="google" redirectPath={pathname} />;
    $[0] = pathname;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isAppleHidden || $[5] !== pathname) {
    t4 = isAppleHidden ? null : <SocialLink type="apple" redirectPath={pathname} />;
    $[4] = isAppleHidden;
    $[5] = pathname;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className="hidden w-full max-w-[335px] flex-col gap-12 xl:flex">{t1}{t2}{t3}{t4}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== pathname) {
    t6 = <SocialLink type="kakao" redirectPath={pathname} />;
    $[12] = pathname;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== isAppleHidden || $[15] !== pathname) {
    t7 = isAppleHidden ? null : <SocialLink type="apple" redirectPath={pathname} />;
    $[14] = isAppleHidden;
    $[15] = pathname;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="flex flex-col gap-12">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex items-center gap-12"><div className="flex-1 border-t border-Gray-200" /><span className="font-700 text-Gray-500 typo-caption1">또는</span><div className="flex-1 border-t border-Gray-200" /></div>;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== pathname) {
    t10 = <div className="flex justify-center gap-12"><SocialLink isIconOnly={true} type="naver" redirectPath={pathname} /><SocialLink isIconOnly={true} type="google" redirectPath={pathname} /></div>;
    $[21] = pathname;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8) {
    t11 = <div className="flex w-full flex-col gap-20 xl:hidden">{t8}{t9}{t10}</div>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== onClose || $[27] !== t11 || $[28] !== t5) {
    t12 = <PartnersBlurOverlay isSheetOnMobile={true} title={"\uC544\uC9C1 \uBABB \uBC1B\uC740 \uD61C\uD0DD\uC774\n\uAE30\uB2E4\uB9AC\uACE0 \uC788\uC5B4\uC694"} description={"\uC815\uCC45 \uD61C\uD0DD\uBD80\uD130 \uB2F5\uB840\uD488\uAE4C\uC9C0, \uD558\uB098\uC758 \uACC4\uC815\uC73C\uB85C"} onClose={onClose}>{t5}{t11}</PartnersBlurOverlay>;
    $[26] = onClose;
    $[27] = t11;
    $[28] = t5;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
function _temp(state) {
  return state.osType === "android";
}